// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Which screen does a declaration of an installed overlay open, and where does
 * it sit in the menu?
 *
 * ## The split this file draws
 *
 * An optional overlay pack DECLARES the things registers discover by reading
 * the organisation's installed packs. The overlay catalog endpoint returns them
 * per pack, read off the pack's own content by the backend:
 *
 * - `register_surfaces` — a deadline record's `event_type` and a form
 *   template's `register_surface`;
 * - `pack_declarations.evidence_domains` — the `domain` of each evidence regime
 *   a framework record declares (manufacturing, logistics, public data,
 *   payments, platform evidence);
 * - `pack_declarations.trust_regimes` — the trust and identity evidence regimes;
 * - `pack_declarations.fine_area_count` — the fine areas the pack authors, which
 *   the penalty readiness, exposure and enforcement screens read;
 * - `pack_declarations.fine_area_modules` — the module codes (`ApplicableModule`)
 *   those fine areas name, in whose register the penalty readiness map finds
 *   the evidence.
 *
 * **A pack declares no route, no label and no menu placement** — so those three
 * are the one thing this registry owns, and it is keyed by DECLARATION (a
 * surface kind), never by a framework code, an instrument number or an
 * authority (R-46). Adding an instrument to a pack adds nothing here; adding a
 * new kind of register does.
 *
 * ## What is NOT here, on purpose
 *
 * A sidebar leaf whose visibility depends on no installed overlay is not
 * attached to one. {@link OVERLAY_INDEPENDENT_VIEWS} lists each with the
 * evidence for that finding, so the omission is a recorded decision the test
 * holds, not a gap.
 *
 * ## What is held to account
 *
 * `__tests__/overlay-register-surfaces.test.ts` fails when:
 *
 * - a selector a backend register reads (`BOUNDARY_STATEMENT_READERS`) has no
 *   entry, so a new register cannot ship without a row;
 * - an evidence domain, or a register-bearing module code, has no entry, so a
 *   new one cannot ship without a row either;
 * - a sidebar leaf under the screens this registry covers is neither an entry
 *   nor an independent view;
 * - an entry's route is not a sidebar leaf in `AdminLayout.tsx` or has no
 *   `<Route>` in `App.tsx`;
 * - an entry's label differs from the sidebar leaf's own name, or a label or
 *   menu path does not resolve to a string in both `en` and `vi`.
 *
 * ## Menu placement
 *
 * `menu` is an ordered list of i18n paths — section first, then its group —
 * resolved against `t` by the caller, so the modal reuses the sidebar's own
 * translated names wherever they exist as keys. The groups the sidebar names
 * inline (not as keys) have their own keys under `localization.sector_overlay`.
 *
 * ## Module 30, Sector Overlays (Sprint 63.43.0, owner ruling OR-26)
 *
 * Each view carries the disposition of the plan's 92-row matrix
 * (`docs/product/phases/phase-27/module-30-sector-overlays-plan.md` §2.1):
 *
 * - **Class A, overlay-exclusive** — every pack that brings the view into use is
 *   an optional overlay. The entry names its `sectorGroup`; its route belongs to
 *   the `sector_overlays` module (`ROUTE_MODULE_MAP`) and its `menu` is
 *   Sector Overlays › the group.
 * - **Class B, shared** — optional overlays and baseline frameworks both bring
 *   it into use. It stays in its core module; `entryPointGroups` names the
 *   sector groups whose Overview section links to it ("Shared views this sector
 *   uses").
 * - **Class C, baseline** and **Class D, core module view** — neither field;
 *   they stay where their package puts them.
 *
 * A sector group is a DISPLAY group (`SectorGroupId`), never a framework code,
 * an instrument or an authority (R-46): an overlay is placed in a group by the
 * views it declares into, not by its name. The test holds the 92-row
 * disposition as data, so a reclassification is a reviewed diff there and here.
 */

import {
  type ApplicableModule,
  type EvidenceDomain,
  AML_REGISTER_SURFACES,
  AUTHORITY_EVENT_CLASSIFICATION_EVENT_TYPE,
  BIOMETRIC_CAPTURE_REGISTER_SURFACES,
  CHEMICAL_TRACEABILITY_DECLARATION_EVENT_TYPE,
  CHEMICAL_TRACEABILITY_TERMINATION_EVENT_TYPE,
  CONFORMITY_CERTIFICATE_REGISTER_SURFACE,
  CONSENT_EVIDENCE_REGISTER_SURFACE,
  CONTROLLED_TESTING_PERSONNEL_REGISTER_SURFACE,
  CONTROLLED_TESTING_REGISTRY_REGISTER_SURFACE,
  CONTROLLED_TESTING_SOLUTION_REGISTER_SURFACE,
  COVERED_SCOPE_QUESTIONNAIRE_EVENT_TYPE,
  CREDIT_INFORMATION_SUPPLY_EVENT_TYPE,
  CREDIT_INFORMATION_SUPPLY_REGISTER_SURFACE,
  CREDIT_INFORMATION_USE_REGISTER_SURFACE,
  CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES,
  DATA_EXPLOITATION_REGISTER_SURFACES,
  CUSTOMS_TAX_REGISTER_SURFACES,
  FOOD_TRACEABILITY_PACK_FORM_SURFACES,
  FOOD_TRACEABILITY_RECORDS_REQUEST_EVENT_TYPE,
  FOOD_TRACEABILITY_REGISTER_SURFACES,
  DATA_SERVICE_PROVIDER_EVENT_TYPE,
  DATA_SERVICE_PROVIDER_REGISTER_SURFACE,
  DIRECTION_RESPONSE_EVENT_TYPE,
  DUAL_USE_REFERRAL_REGISTER_SURFACE,
  IDENTITY_EXCHANGE_REGISTER_SURFACE,
  ISSUED_RECORD_LIFECYCLE_EVENT_TYPE,
  ISSUED_RECORD_RETENTION_EVENT_TYPE,
  ISSUED_RECORD_SCOPE_EVENT_TYPE,
  ISSUED_RECORD_SHARING_BASIS_EVENT_TYPE,
  MARKETING_COMMUNICATIONS_EVENT_TYPE,
  MARKETING_CONSENT_REGISTER_SURFACE,
  MARKETING_SUPPRESSION_ENFORCEMENT_REGISTER_SURFACE,
  MARKETING_SUPPRESSION_REGISTER_SURFACE,
  PCF_SUPERVISION_REGISTER_SURFACES,
  PRODUCT_CLASSIFICATION_REGISTER_SURFACE,
  PUBLICATION_ASSESSMENT_CATEGORY_EVENT_TYPE,
  PUBLICATION_RELEASE_GATE_EVENT_TYPE,
  REGULATED_ACCOUNT_LIFECYCLE_EVENT_TYPE,
  REGULATORY_FEE_SCHEDULE_REGISTER_SURFACE,
  REGULATORY_PLACEHOLDER_EVENT_TYPE,
  SECTOR_LICENCE_LIFECYCLE_EVENT_TYPE,
  SUPERVISORY_REPORTING_REGISTER_SURFACE,
} from '@vn-grc/shared';
import { getRouteModule, routeOpensFor } from './route-permissions.js';

/**
 * Why a view is listed for a pack — the kind of declaration that joined them.
 *
 * - `records` — the pack declares the records the register is made of (a
 *   deadline `event_type` or a form `register_surface`);
 * - `regime` — the pack declares the evidence or trust regime the screen reads;
 * - `fine_areas` — the pack authors fine areas this screen prices, tracks, or
 *   evidences from its records.
 */
export type OverlayViewBasis = 'records' | 'regime' | 'fine_areas';

/** The pack-side facts a catalog entry carries (see the header). */
export interface OverlayPackFacts {
  register_surfaces?: readonly string[];
  pack_declarations?: {
    evidence_domains?: readonly string[];
    trust_regimes?: boolean;
    fine_area_count?: number;
    fine_area_modules?: readonly string[];
  };
}

/** One screen an overlay's declarations can bring into use. */
export interface OverlayRegisterEntry {
  /** Stable id (also the React key). */
  id: string;
  /**
   * The selectors that, declared by an installed pack, make this register
   * meaningful. A view is listed for a pack when the pack declares ANY of them
   * (or any other declaration below). `[]` for a screen joined by another kind.
   */
  selectors: readonly string[];
  /**
   * Evidence-regime domains the screen reads: it records under, or offers, the
   * regimes an installed framework declares in one of these domains.
   */
  evidenceDomains?: readonly EvidenceDomain[];
  /** The screen reads the trust regimes an installed framework declares. */
  trustRegimes?: boolean;
  /**
   * Fine-area module codes whose evidence this register holds. A pack whose
   * fine areas name one of these is evidenced from this register's records
   * (`MODULE_EVIDENCE_SOURCES` in the penalty-exposure service).
   */
  fineAreaModules?: readonly ApplicableModule[];
  /** The screen reads every installed framework's fine areas. */
  fineAreas?: true;
  /** Absolute app route — a real `<Route>` and a sidebar leaf. */
  to: string;
  /** i18n path (against `t`) of the view's name — the sidebar leaf's name. */
  label: string;
  /** i18n paths (against `t`), outermost first: the sidebar section, then its group. */
  menu: readonly string[];
  /**
   * Class A (overlay-exclusive) only: the Sector Overlays group the view sits in.
   * Its route resolves to the `sector_overlays` module.
   */
  sectorGroup?: SectorGroupId;
  /**
   * Class B (shared) only: the Sector Overlays groups whose Overview section
   * links to this core view as a shared entry point, in `SECTOR_GROUP_ORDER`.
   */
  entryPointGroups?: readonly SectorGroupId[];
}

/**
 * The display groups of Module 30, Sector Overlays (plan §3.1). A display id,
 * never a framework code (R-46).
 */
export type SectorGroupId =
  | 'banking_payments'
  | 'crypto_assets'
  | 'manufacturing'
  | 'food_traceability'
  | 'transport_logistics'
  | 'public_administration'
  | 'digital_technology'
  | 'publishing'
  | 'education'
  | 'cross_sector';

/** The order the groups render in, in the sidebar and on the Overview page (plan §3.1). */
export const SECTOR_GROUP_ORDER: readonly SectorGroupId[] = [
  'banking_payments',
  'crypto_assets',
  'manufacturing',
  'food_traceability',
  'transport_logistics',
  'public_administration',
  'digital_technology',
  'publishing',
  'education',
  'cross_sector',
];

/** i18n path (against `t`) of each group's name — the sidebar group's label. */
export const SECTOR_GROUP_LABEL_KEYS: Readonly<Record<SectorGroupId, string>> = {
  banking_payments: 'admin.sidebar_groups.sector_overlays_banking_payments',
  crypto_assets: 'admin.sidebar_groups.sector_overlays_crypto_assets',
  manufacturing: 'admin.sidebar_groups.sector_overlays_manufacturing',
  food_traceability: 'admin.sidebar_groups.sector_overlays_food_traceability',
  transport_logistics: 'admin.sidebar_groups.sector_overlays_transport_logistics',
  public_administration: 'admin.sidebar_groups.sector_overlays_public_administration',
  digital_technology: 'admin.sidebar_groups.sector_overlays_digital_technology',
  publishing: 'admin.sidebar_groups.sector_overlays_publishing',
  education: 'admin.sidebar_groups.sector_overlays_education',
  cross_sector: 'admin.sidebar_groups.sector_overlays_cross_sector',
};

/** i18n path of the Sector Overlays section's name (also the module's label). */
export const SECTOR_OVERLAYS_SECTION_LABEL_KEY = 'module_labels.sector_overlays';
/** i18n path of the Overlay Management group (Overview and Overlay Catalog). */
export const SECTOR_OVERLAYS_MANAGEMENT_LABEL_KEY =
  'admin.sidebar_groups.sector_overlays_management';
/** The Sector Overlays Overview page — the section's first leaf. */
export const SECTOR_OVERLAYS_OVERVIEW_ROUTE = '/admin/sector-overlays';
/** The Overlay Catalog (installer) — relocated in the menu, URL unchanged. */
export const SECTOR_OVERLAYS_INSTALLER_ROUTE = '/admin/localization/sector-overlays';

/** The menu path of a Class A view: Sector Overlays › its group. */
function sectorMenu(group: SectorGroupId): readonly string[] {
  return [SECTOR_OVERLAYS_SECTION_LABEL_KEY, SECTOR_GROUP_LABEL_KEYS[group]];
}

// Sprint 63.43.0 — the old Localization › Sector Overlays group is gone: its
// shared and baseline registers sit in Regulatory Registers, the screens that read
// every installed fine area in Penalties & Enforcement (plan §3.2), and its
// overlay-exclusive registers under Sector Overlays (`sectorMenu`).
// Sprint 63.42.2 (AC3) — those groups and Data Exploitation are in the Regulatory
// section now (`admin.nav.regulatory`), no longer under Localization.
const REGULATORY_REGISTERS_MENU = [
  'admin.nav.regulatory',
  'admin.sidebar_groups.localization_regulatory_registers',
] as const;
const PENALTIES_ENFORCEMENT_MENU = [
  'admin.nav.regulatory',
  'admin.sidebar_groups.localization_penalties_enforcement',
] as const;
const DATA_EXPLOITATION_MENU = [
  'admin.nav.regulatory',
  'admin.sidebar_groups.localization_data_exploitation',
] as const;
const TRUST_EVIDENCE_MENU = [
  'admin.nav.governance',
  'localization.sector_overlay.menu_trust_evidence',
] as const;
const PRODUCT_COMPLIANCE_MENU = [
  'admin.nav.program_governance',
  'localization.sector_overlay.menu_product_compliance',
] as const;
const TRADE_CUSTOMS_MENU = [
  'admin.nav.program_governance',
  'localization.sector_overlay.menu_trade_customs',
] as const;
const VIEW_LABEL = 'localization.sector_overlay.view_labels';

/**
 * The two deadline `event_type`s the rights-request clocks are read by
 * (`src/backend/src/modules/sla/rights-deadline-resolver.ts`). A selector, not an
 * instrument: any installed framework may declare them.
 */
const RIGHTS_REQUEST_ACKNOWLEDGMENT_EVENT_TYPE = 'rights_request_acknowledgment';
// Exported (Sprint 63.33.0): Settings › SLA Deadlines lists the records that
// date a request type by this selector, the one the resolver dates them by.
export const RIGHTS_REQUEST_FULFILLMENT_EVENT_TYPE = 'rights_request_fulfillment';

/**
 * The deadline `event_type` the incident notification clocks are read by besides
 * the two notification duties (`INCIDENT_PERIOD_EVENT_TYPE` in
 * `incident-ops/breach-timeline-templates.ts`, one of `INCIDENT_CLOCK_EVENT_TYPES`).
 * A selector, not an instrument: any installed framework may declare it.
 */
const INCIDENT_PERIOD_EVENT_TYPE = 'incident_clock';

export const OVERLAY_REGISTER_VIEWS: readonly OverlayRegisterEntry[] = [
  // ── Sector Overlays: registers a pack's records fill ──────────────────
  {
    id: 'credit_information_supply',
    selectors: [CREDIT_INFORMATION_SUPPLY_EVENT_TYPE, CREDIT_INFORMATION_SUPPLY_REGISTER_SURFACE],
    to: '/admin/credit-information-supply',
    label: `${VIEW_LABEL}.credit_information_supply`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    // Sprint 63.24.0. The case page's registry, personnel and solution sections
    // offer the forms an installed pack declares under these three surfaces
    // (`controlled-testing/registry-vocabulary.ts`, `readPackFormsBySurface`).
    id: 'controlled_testing_cases',
    selectors: [
      CONTROLLED_TESTING_REGISTRY_REGISTER_SURFACE,
      CONTROLLED_TESTING_PERSONNEL_REGISTER_SURFACE,
      CONTROLLED_TESTING_SOLUTION_REGISTER_SURFACE,
    ],
    to: '/admin/controlled-testing/cases',
    label: `${VIEW_LABEL}.controlled_testing_cases`,
    menu: REGULATORY_REGISTERS_MENU,
    entryPointGroups: ['banking_payments'],
  },
  {
    // fix-sprint-20261016-2013 Finding 10. The rights-request clocks are read off
    // any installed framework's deadline rows by these two event types
    // (`sla/rights-deadline-resolver.ts`), so an overlay that declares them sets
    // the acknowledgment and fulfilment dates on the Rights Requests screen.
    id: 'rights_requests',
    selectors: [RIGHTS_REQUEST_ACKNOWLEDGMENT_EVENT_TYPE, RIGHTS_REQUEST_FULFILLMENT_EVENT_TYPE],
    to: '/admin/rights-requests',
    label: 'admin.nav.rights_requests',
    menu: ['admin.nav.rights_requests'],
  },
  {
    // Merging `main` (2026-10-24). The breach-notification clocks are read off any
    // installed framework's deadline rows whose `event_type` is an incident clock
    // (`incidents/incident-response-maturity-service.ts`), so an overlay that
    // declares the incident period (the Decree 386 incident-notice prompt) puts a
    // clock on the Breach Notifications screen.
    id: 'incident_notification_clocks',
    selectors: [INCIDENT_PERIOD_EVENT_TYPE],
    to: '/admin/incidents/breach-notifications',
    label: 'incidents.nav.breach_notifications',
    menu: ['admin.nav.incidents'],
  },
  {
    id: 'consent_evidence',
    selectors: [CONSENT_EVIDENCE_REGISTER_SURFACE],
    to: '/admin/consent/evidence',
    label: 'consent.nav.evidence',
    menu: ['admin.nav.consent'],
  },
  {
    id: 'credit_information_uses',
    selectors: [CREDIT_INFORMATION_USE_REGISTER_SURFACE],
    to: '/admin/credit-information/uses',
    label: 'credit_information.title',
    menu: ['admin.nav.assessments', 'admin.sidebar_groups.pg_privacy_office'],
  },
  {
    id: 'supervisory_reporting',
    selectors: [SUPERVISORY_REPORTING_REGISTER_SURFACE],
    to: '/admin/payments-evidence/supervisory-reporting',
    label: `${VIEW_LABEL}.supervisory_reporting`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'aml_programme',
    selectors: [
      AML_REGISTER_SURFACES.scoping,
      AML_REGISTER_SURFACES.indicatorCoverage,
      AML_REGISTER_SURFACES.beneficialOwnership,
      AML_REGISTER_SURFACES.preLaunch,
      AML_REGISTER_SURFACES.retentionScope,
    ],
    to: '/admin/payments-evidence/aml-programme',
    label: `${VIEW_LABEL}.aml_programme`,
    menu: sectorMenu('crypto_assets'),
    sectorGroup: 'crypto_assets',
  },
  {
    id: 'aml_cases',
    selectors: [AML_REGISTER_SURFACES.case],
    to: '/admin/payments-evidence/aml-cases',
    label: `${VIEW_LABEL}.aml_cases`,
    menu: sectorMenu('crypto_assets'),
    sectorGroup: 'crypto_assets',
  },
  {
    id: 'authority_directions',
    // The removal-direction page carries the scoping questionnaire panel, which
    // discovers its questionnaire from `covered_scope_questionnaire`.
    selectors: [
      DIRECTION_RESPONSE_EVENT_TYPE,
      AUTHORITY_EVENT_CLASSIFICATION_EVENT_TYPE,
      COVERED_SCOPE_QUESTIONNAIRE_EVENT_TYPE,
    ],
    fineAreaModules: ['authority_direction'],
    to: '/admin/authority-directions',
    label: `${VIEW_LABEL}.authority_directions`,
    menu: REGULATORY_REGISTERS_MENU,
    entryPointGroups: ['banking_payments', 'crypto_assets', 'publishing'],
  },
  {
    id: 'regulated_accounts',
    selectors: [REGULATED_ACCOUNT_LIFECYCLE_EVENT_TYPE],
    to: '/admin/regulated-accounts',
    label: `${VIEW_LABEL}.regulated_accounts`,
    menu: sectorMenu('transport_logistics'),
    sectorGroup: 'transport_logistics',
  },
  {
    id: 'sector_licences',
    selectors: [SECTOR_LICENCE_LIFECYCLE_EVENT_TYPE],
    fineAreaModules: ['sector_licensing'],
    to: '/admin/sector-licences',
    label: `${VIEW_LABEL}.sector_licences`,
    menu: REGULATORY_REGISTERS_MENU,
    entryPointGroups: ['crypto_assets'],
  },
  {
    // Sprint 62.54.0 — applications and their fee lines read the installed
    // fee schedule, which a pack declares as a form template's
    // `register_surface`. The sender-name side of the same schedule is
    // `sender_name_certificates` below.
    id: 'licence_applications',
    selectors: [REGULATORY_FEE_SCHEDULE_REGISTER_SURFACE],
    to: '/admin/sector-licences/applications',
    label: `${VIEW_LABEL}.licence_applications`,
    menu: sectorMenu('digital_technology'),
    sectorGroup: 'digital_technology',
  },
  {
    // Sprint 62.55.0 (UC-VN-109 F-3) — the period view reads the lines recorded
    // against the same installed fee schedule, across applications and
    // sender-name certificates.
    id: 'fee_lines_by_period',
    selectors: [REGULATORY_FEE_SCHEDULE_REGISTER_SURFACE],
    to: '/admin/sector-licences/fee-lines',
    label: `${VIEW_LABEL}.fee_lines_by_period`,
    menu: sectorMenu('digital_technology'),
    sectorGroup: 'digital_technology',
  },
  {
    // The marketing-claim gate is a rule over the licence register: an approval
    // names a licence kind, and the kinds exist only where an installed pack
    // declares them (`marketing-approval-service.ts`, "Fail-closed" paragraph).
    // So it is joined by the same selector the licence register discovers its
    // kinds with.
    id: 'marketing_approvals',
    selectors: [SECTOR_LICENCE_LIFECYCLE_EVENT_TYPE],
    to: '/admin/marketing-approvals',
    label: `${VIEW_LABEL}.marketing_approvals`,
    menu: REGULATORY_REGISTERS_MENU,
    entryPointGroups: ['crypto_assets'],
  },
  {
    // The three investor-protection registers hold records of their own; what a
    // pack declares for them is the fine areas whose evidence they are. A fine
    // area naming the module code is evidenced from this register's records
    // (`MODULE_EVIDENCE_SOURCES`), and the register's citation picker offers
    // that pack's fine areas.
    id: 'investor_verifications',
    selectors: [],
    fineAreaModules: ['investor_verification'],
    to: '/admin/investor-verifications',
    label: `${VIEW_LABEL}.investor_verifications`,
    menu: sectorMenu('crypto_assets'),
    sectorGroup: 'crypto_assets',
  },
  {
    id: 'account_data_disclosures',
    selectors: [],
    fineAreaModules: ['account_data_governance'],
    to: '/admin/account-data-disclosures',
    label: `${VIEW_LABEL}.account_data_disclosures`,
    menu: sectorMenu('crypto_assets'),
    sectorGroup: 'crypto_assets',
  },
  {
    id: 'regulatory_disclosures',
    selectors: [],
    fineAreaModules: ['regulatory_disclosure'],
    to: '/admin/regulatory-disclosures',
    label: `${VIEW_LABEL}.regulatory_disclosures`,
    menu: REGULATORY_REGISTERS_MENU,
    entryPointGroups: ['crypto_assets'],
  },
  {
    id: 'data_service_providers',
    selectors: [DATA_SERVICE_PROVIDER_EVENT_TYPE, DATA_SERVICE_PROVIDER_REGISTER_SURFACE],
    to: '/admin/data-service-providers',
    label: `${VIEW_LABEL}.data_service_providers`,
    menu: REGULATORY_REGISTERS_MENU,
  },
  // Sprint 62.50.0 — the supervision-information registers. Each page reads the
  // installed templates that declare its surface (`GET /pcf-supervision/context`);
  // the System records page reads five, one per tab.
  {
    id: 'pcf_shared_information',
    // The written requests and proposals (Sprint 62.50.1) are sections of the
    // same page, below the shared-information entries.
    selectors: [
      PCF_SUPERVISION_REGISTER_SURFACES.sharedInformation,
      PCF_SUPERVISION_REGISTER_SURFACES.writtenRequest,
      PCF_SUPERVISION_REGISTER_SURFACES.changeProposal,
    ],
    to: '/admin/pcf-supervision/shared-information',
    label: `${VIEW_LABEL}.pcf_shared_information`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'pcf_branch_publicity',
    selectors: [PCF_SUPERVISION_REGISTER_SURFACES.publicity],
    to: '/admin/pcf-supervision/publicity',
    label: `${VIEW_LABEL}.pcf_branch_publicity`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'pcf_branch_information_requests',
    selectors: [PCF_SUPERVISION_REGISTER_SURFACES.informationRequest],
    to: '/admin/pcf-supervision/information-requests',
    label: `${VIEW_LABEL}.pcf_branch_information_requests`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'pcf_depositor_enquiries',
    selectors: [PCF_SUPERVISION_REGISTER_SURFACES.depositorEnquiry],
    to: '/admin/pcf-supervision/depositor-enquiries',
    label: `${VIEW_LABEL}.pcf_depositor_enquiries`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'pcf_supervision_system_records',
    selectors: [
      PCF_SUPERVISION_REGISTER_SURFACES.reportingCalendar,
      PCF_SUPERVISION_REGISTER_SURFACES.incident,
      PCF_SUPERVISION_REGISTER_SURFACES.correction,
      PCF_SUPERVISION_REGISTER_SURFACES.systemOperation,
      PCF_SUPERVISION_REGISTER_SURFACES.connectionReadiness,
    ],
    to: '/admin/pcf-supervision/system-records',
    label: `${VIEW_LABEL}.pcf_supervision_system_records`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  // Sprint 62.51.0 — the data-exploitation registers, each page reading the
  // installed templates that declare its surface (`GET /data-exploitation/context`).
  // The production-reuse check is a sub-record of the testing record and lives
  // on the testing page.
  {
    id: 'data_exploitation_routes',
    selectors: [DATA_EXPLOITATION_REGISTER_SURFACES.activity],
    to: '/admin/data-exploitation/routes',
    label: `${VIEW_LABEL}.data_exploitation_routes`,
    menu: DATA_EXPLOITATION_MENU,
  },
  {
    id: 'data_exploitation_requests',
    selectors: [DATA_EXPLOITATION_REGISTER_SURFACES.request],
    to: '/admin/data-exploitation/requests',
    label: `${VIEW_LABEL}.data_exploitation_requests`,
    menu: DATA_EXPLOITATION_MENU,
  },
  {
    id: 'data_exploitation_testing',
    selectors: [
      DATA_EXPLOITATION_REGISTER_SURFACES.testing,
      DATA_EXPLOITATION_REGISTER_SURFACES.reuseCheck,
    ],
    to: '/admin/data-exploitation/testing',
    label: `${VIEW_LABEL}.data_exploitation_testing`,
    menu: DATA_EXPLOITATION_MENU,
  },
  {
    id: 'data_exploitation_notices',
    selectors: [DATA_EXPLOITATION_REGISTER_SURFACES.notice],
    to: '/admin/data-exploitation/notices',
    label: `${VIEW_LABEL}.data_exploitation_notices`,
    menu: DATA_EXPLOITATION_MENU,
  },
  {
    id: 'data_exploitation_dispositions',
    selectors: [DATA_EXPLOITATION_REGISTER_SURFACES.disposition],
    to: '/admin/data-exploitation/dispositions',
    label: `${VIEW_LABEL}.data_exploitation_dispositions`,
    menu: DATA_EXPLOITATION_MENU,
  },
  {
    id: 'data_exploitation_incubator',
    selectors: [DATA_EXPLOITATION_REGISTER_SURFACES.incubator],
    to: '/admin/data-exploitation/incubator',
    label: `${VIEW_LABEL}.data_exploitation_incubator`,
    menu: DATA_EXPLOITATION_MENU,
  },
  {
    id: 'data_exploitation_foreign_cooperation',
    selectors: [DATA_EXPLOITATION_REGISTER_SURFACES.foreignAssessment],
    to: '/admin/data-exploitation/foreign-cooperation',
    label: `${VIEW_LABEL}.data_exploitation_foreign_cooperation`,
    menu: DATA_EXPLOITATION_MENU,
  },
  // Sprint 62.52.0 — the cybersecurity assurance index registers, each reading
  // the installed templates that declare its surface (`GET
  // /cybersecurity-assurance-index/context`). The catalogue surface is read by the
  // period's item table, reached from Assessment Periods.
  {
    id: 'cybersecurity_assurance_index_periods',
    selectors: [
      CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES.period,
      CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES.catalogue,
    ],
    to: '/admin/cybersecurity-assurance-index/periods',
    label: `${VIEW_LABEL}.cybersecurity_assurance_index_periods`,
    menu: sectorMenu('cross_sector'),
    sectorGroup: 'cross_sector',
  },
  {
    id: 'cybersecurity_assurance_index_statistics',
    selectors: [CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES.statistic],
    to: '/admin/cybersecurity-assurance-index/statistics',
    label: `${VIEW_LABEL}.cybersecurity_assurance_index_statistics`,
    menu: sectorMenu('cross_sector'),
    sectorGroup: 'cross_sector',
  },
  {
    id: 'cybersecurity_assurance_index_supplier_requests',
    selectors: [CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES.supplierRequest],
    to: '/admin/cybersecurity-assurance-index/supplier-requests',
    label: `${VIEW_LABEL}.cybersecurity_assurance_index_supplier_requests`,
    menu: sectorMenu('cross_sector'),
    sectorGroup: 'cross_sector',
  },
  // Sprint 62.49.0 — the food traceability registers (`GET /food-traceability/context`).
  // The establishment and certificate templates carry the registers' vocabularies;
  // a register with no template of its own (inspections, warnings, decisions,
  // corrections, complaints, routes) is in use while an installed, operative
  // framework declares either surface. The records-request clock (its deadline
  // `event_type`) and the records-request log are read by the establishment page's
  // records-request view; the report layout and the recall template's trigger
  // sources by the recall notices; the lot record by the lot results.
  {
    id: 'food_traceability_establishment',
    selectors: [
      FOOD_TRACEABILITY_REGISTER_SURFACES.establishment,
      FOOD_TRACEABILITY_REGISTER_SURFACES.certificate,
      FOOD_TRACEABILITY_RECORDS_REQUEST_EVENT_TYPE,
      FOOD_TRACEABILITY_PACK_FORM_SURFACES.recordsRequestLog,
    ],
    to: '/admin/food-traceability/establishment',
    label: `${VIEW_LABEL}.food_traceability_establishment`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },
  {
    id: 'food_traceability_lot_results',
    selectors: [FOOD_TRACEABILITY_PACK_FORM_SURFACES.lotRecord],
    to: '/admin/food-traceability/lot-results',
    label: `${VIEW_LABEL}.food_traceability_lot_results`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },
  {
    id: 'food_traceability_inspections',
    selectors: [
      FOOD_TRACEABILITY_REGISTER_SURFACES.establishment,
      FOOD_TRACEABILITY_REGISTER_SURFACES.certificate,
    ],
    to: '/admin/food-traceability/inspections',
    label: `${VIEW_LABEL}.food_traceability_inspections`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },
  {
    id: 'food_traceability_warnings',
    selectors: [
      FOOD_TRACEABILITY_REGISTER_SURFACES.establishment,
      FOOD_TRACEABILITY_REGISTER_SURFACES.certificate,
    ],
    to: '/admin/food-traceability/warnings',
    label: `${VIEW_LABEL}.food_traceability_warnings`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },
  {
    id: 'food_traceability_notices',
    selectors: [
      FOOD_TRACEABILITY_PACK_FORM_SURFACES.annexReport,
      FOOD_TRACEABILITY_PACK_FORM_SURFACES.recall,
    ],
    to: '/admin/food-traceability/notices',
    label: `${VIEW_LABEL}.food_traceability_notices`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },
  {
    id: 'food_traceability_decisions',
    selectors: [
      FOOD_TRACEABILITY_REGISTER_SURFACES.establishment,
      FOOD_TRACEABILITY_REGISTER_SURFACES.certificate,
    ],
    to: '/admin/food-traceability/decisions',
    label: `${VIEW_LABEL}.food_traceability_decisions`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },
  {
    id: 'food_traceability_corrections',
    selectors: [
      FOOD_TRACEABILITY_REGISTER_SURFACES.establishment,
      FOOD_TRACEABILITY_REGISTER_SURFACES.certificate,
    ],
    to: '/admin/food-traceability/corrections',
    label: `${VIEW_LABEL}.food_traceability_corrections`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },
  {
    id: 'food_traceability_complaints',
    selectors: [
      FOOD_TRACEABILITY_REGISTER_SURFACES.establishment,
      FOOD_TRACEABILITY_REGISTER_SURFACES.certificate,
    ],
    to: '/admin/food-traceability/complaints',
    label: `${VIEW_LABEL}.food_traceability_complaints`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },
  {
    id: 'food_traceability_routes',
    selectors: [
      FOOD_TRACEABILITY_REGISTER_SURFACES.establishment,
      FOOD_TRACEABILITY_REGISTER_SURFACES.certificate,
    ],
    to: '/admin/food-traceability/routes',
    label: `${VIEW_LABEL}.food_traceability_routes`,
    menu: sectorMenu('food_traceability'),
    sectorGroup: 'food_traceability',
  },

  // Sprint 62.56.0 — the biometric capture registers. Each reads the installed
  // templates that declare its surface (`GET /biometric-capture/context`); the
  // component detail page is reached from the components list and is no leaf.
  {
    id: 'biometric_capture_components',
    // Round 2 (O-3a): a component's deletion records are a section of its detail page.
    selectors: [
      BIOMETRIC_CAPTURE_REGISTER_SURFACES.component,
      BIOMETRIC_CAPTURE_REGISTER_SURFACES.deletionRecord,
    ],
    to: '/admin/biometric-capture/components',
    label: `${VIEW_LABEL}.biometric_capture_components`,
    menu: sectorMenu('public_administration'),
    sectorGroup: 'public_administration',
  },
  {
    id: 'biometric_chain_of_custody',
    selectors: [BIOMETRIC_CAPTURE_REGISTER_SURFACES.custodyTransfer],
    to: '/admin/biometric-capture/custody-transfers',
    label: `${VIEW_LABEL}.biometric_chain_of_custody`,
    menu: sectorMenu('public_administration'),
    sectorGroup: 'public_administration',
  },
  {
    id: 'biometric_readiness',
    selectors: [BIOMETRIC_CAPTURE_REGISTER_SURFACES.readinessReview],
    to: '/admin/biometric-capture/readiness',
    label: `${VIEW_LABEL}.biometric_readiness`,
    menu: sectorMenu('public_administration'),
    sectorGroup: 'public_administration',
  },
  // Round 2 (PU-4) — the supervision and inspection acts.
  {
    id: 'biometric_supervision_records',
    selectors: [BIOMETRIC_CAPTURE_REGISTER_SURFACES.supervisionAct],
    to: '/admin/biometric-capture/supervision-records',
    label: `${VIEW_LABEL}.biometric_supervision_records`,
    menu: sectorMenu('public_administration'),
    sectorGroup: 'public_administration',
  },
  {
    id: 'product_classifications',
    selectors: [PRODUCT_CLASSIFICATION_REGISTER_SURFACE, DUAL_USE_REFERRAL_REGISTER_SURFACE],
    to: '/admin/product-classifications',
    label: `${VIEW_LABEL}.product_classifications`,
    menu: REGULATORY_REGISTERS_MENU,
  },
  {
    id: 'conformity_certificates',
    selectors: [CONFORMITY_CERTIFICATE_REGISTER_SURFACE],
    to: '/admin/conformity-certificates',
    label: `${VIEW_LABEL}.conformity_certificates`,
    menu: REGULATORY_REGISTERS_MENU,
  },
  {
    id: 'student_records',
    selectors: [
      ISSUED_RECORD_LIFECYCLE_EVENT_TYPE,
      ISSUED_RECORD_RETENTION_EVENT_TYPE,
      ISSUED_RECORD_SCOPE_EVENT_TYPE,
      ISSUED_RECORD_SHARING_BASIS_EVENT_TYPE,
    ],
    fineAreaModules: ['issued_record'],
    to: '/admin/student-records',
    label: `${VIEW_LABEL}.student_records`,
    menu: sectorMenu('education'),
    sectorGroup: 'education',
  },
  {
    id: 'publications',
    selectors: [PUBLICATION_RELEASE_GATE_EVENT_TYPE],
    fineAreaModules: ['legal_deposit'],
    to: '/admin/publications',
    label: `${VIEW_LABEL}.publications`,
    menu: sectorMenu('publishing'),
    sectorGroup: 'publishing',
  },
  {
    // The scoping questionnaire sits on this page (`ScopeQuestionnairePanel`),
    // discovered from `covered_scope_questionnaire`.
    id: 'legal_representatives',
    selectors: [COVERED_SCOPE_QUESTIONNAIRE_EVENT_TYPE],
    fineAreaModules: ['legal_representative'],
    to: '/admin/legal-representatives',
    label: `${VIEW_LABEL}.legal_representatives`,
    menu: sectorMenu('publishing'),
    sectorGroup: 'publishing',
  },
  {
    id: 'publication_assessments',
    selectors: [PUBLICATION_ASSESSMENT_CATEGORY_EVENT_TYPE],
    fineAreaModules: ['publication_assessment'],
    to: '/admin/publication-assessments',
    label: `${VIEW_LABEL}.publication_assessments`,
    menu: sectorMenu('publishing'),
    sectorGroup: 'publishing',
  },
  {
    id: 'regulatory_placeholders',
    selectors: [REGULATORY_PLACEHOLDER_EVENT_TYPE],
    to: '/admin/regulatory-placeholders',
    label: `${VIEW_LABEL}.regulatory_placeholders`,
    menu: REGULATORY_REGISTERS_MENU,
  },
  // ── Sector Overlays: the screens that read every installed fine area ──
  {
    id: 'penalty_readiness',
    selectors: [],
    fineAreas: true,
    to: '/admin/penalty-exposure',
    label: `${VIEW_LABEL}.penalty_readiness`,
    menu: PENALTIES_ENFORCEMENT_MENU,
  },
  {
    id: 'exposure_inputs',
    selectors: [],
    fineAreas: true,
    to: '/admin/penalty-exposure/inputs',
    label: `${VIEW_LABEL}.exposure_inputs`,
    menu: PENALTIES_ENFORCEMENT_MENU,
  },
  {
    id: 'enforcement_register',
    selectors: [],
    fineAreas: true,
    to: '/admin/penalty-exposure/register',
    label: `${VIEW_LABEL}.enforcement_register`,
    menu: PENALTIES_ENFORCEMENT_MENU,
  },
  {
    id: 'enforcement_cases',
    selectors: [],
    fineAreas: true,
    to: '/admin/enforcement-cases',
    label: `${VIEW_LABEL}.enforcement_cases`,
    menu: PENALTIES_ENFORCEMENT_MENU,
  },
  // ── Consent: the marketing registers ─────────────────────────────────
  {
    id: 'marketing_consent',
    selectors: [MARKETING_COMMUNICATIONS_EVENT_TYPE, MARKETING_CONSENT_REGISTER_SURFACE],
    to: '/admin/marketing-communications/consents',
    label: 'consent.nav.marketing_consent',
    menu: ['admin.nav.consent', 'admin.sidebar_groups.marketing_communications'],
  },
  {
    id: 'marketing_suppression',
    selectors: [MARKETING_COMMUNICATIONS_EVENT_TYPE, MARKETING_SUPPRESSION_REGISTER_SURFACE],
    to: '/admin/marketing-communications/suppressions',
    label: 'consent.nav.marketing_suppression',
    menu: ['admin.nav.consent', 'admin.sidebar_groups.marketing_communications'],
  },
  {
    id: 'marketing_suppression_enforcement',
    selectors: [
      MARKETING_COMMUNICATIONS_EVENT_TYPE,
      MARKETING_SUPPRESSION_ENFORCEMENT_REGISTER_SURFACE,
    ],
    to: '/admin/marketing-communications/enforcement',
    label: 'consent.nav.marketing_suppression_enforcement',
    menu: ['admin.nav.consent', 'admin.sidebar_groups.marketing_communications'],
  },
  {
    // Sprint 62.54.0 — the sender-identity side of the installed fee schedule.
    id: 'sender_name_certificates',
    selectors: [REGULATORY_FEE_SCHEDULE_REGISTER_SURFACE],
    to: '/admin/marketing-communications/sender-name-certificates',
    label: 'consent.nav.marketing_sender_name_certificates',
    menu: sectorMenu('digital_technology'),
    sectorGroup: 'digital_technology',
  },
  // ── Governance: trust, payments and platform evidence ────────────────
  {
    id: 'identity_exchanges',
    selectors: [IDENTITY_EXCHANGE_REGISTER_SURFACE],
    to: '/admin/trust-evidence/identity-exchanges',
    label: `${VIEW_LABEL}.identity_exchanges`,
    menu: TRUST_EVIDENCE_MENU,
  },
  {
    id: 'identity_register',
    selectors: [],
    trustRegimes: true,
    to: '/admin/trust-evidence/identities',
    label: `${VIEW_LABEL}.identity_register`,
    menu: TRUST_EVIDENCE_MENU,
    entryPointGroups: ['transport_logistics', 'cross_sector'],
  },
  {
    id: 'trust_providers',
    selectors: [],
    trustRegimes: true,
    to: '/admin/trust-evidence/providers',
    label: `${VIEW_LABEL}.trust_providers`,
    menu: TRUST_EVIDENCE_MENU,
    entryPointGroups: ['transport_logistics', 'cross_sector'],
  },
  {
    id: 'electronic_submissions',
    selectors: [],
    trustRegimes: true,
    to: '/admin/trust-evidence/submissions',
    label: `${VIEW_LABEL}.electronic_submissions`,
    menu: TRUST_EVIDENCE_MENU,
    entryPointGroups: ['transport_logistics', 'cross_sector'],
  },
  {
    id: 'electronic_agreements',
    selectors: [],
    trustRegimes: true,
    to: '/admin/trust-evidence/agreements',
    label: `${VIEW_LABEL}.electronic_agreements`,
    menu: TRUST_EVIDENCE_MENU,
    entryPointGroups: ['transport_logistics', 'cross_sector'],
  },
  {
    id: 'payment_services',
    selectors: [],
    evidenceDomains: ['payments'],
    to: '/admin/payments-evidence/services',
    label: `${VIEW_LABEL}.payment_services`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'ekyc_evidence',
    selectors: [],
    evidenceDomains: ['payments'],
    to: '/admin/payments-evidence/verifications',
    label: `${VIEW_LABEL}.ekyc_evidence`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'exception_review',
    selectors: [],
    evidenceDomains: ['payments'],
    to: '/admin/payments-evidence/exceptions',
    label: `${VIEW_LABEL}.exception_review`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'retention_reconciliation',
    selectors: [],
    evidenceDomains: ['payments'],
    to: '/admin/payments-evidence/retention',
    label: `${VIEW_LABEL}.retention_reconciliation`,
    menu: sectorMenu('banking_payments'),
    sectorGroup: 'banking_payments',
  },
  {
    id: 'seller_register',
    selectors: [],
    evidenceDomains: ['platform'],
    to: '/admin/platform-evidence/sellers',
    label: `${VIEW_LABEL}.seller_register`,
    menu: sectorMenu('digital_technology'),
    sectorGroup: 'digital_technology',
  },
  {
    id: 'transaction_reconciliation',
    selectors: [],
    evidenceDomains: ['platform'],
    to: '/admin/platform-evidence/reconciliation',
    label: `${VIEW_LABEL}.transaction_reconciliation`,
    menu: sectorMenu('digital_technology'),
    sectorGroup: 'digital_technology',
  },
  {
    id: 'authority_correspondence',
    selectors: [],
    evidenceDomains: ['platform'],
    to: '/admin/platform-evidence/authority-requests',
    label: `${VIEW_LABEL}.authority_correspondence`,
    menu: sectorMenu('digital_technology'),
    sectorGroup: 'digital_technology',
  },
  // ── Data Mapping: public data exchange ───────────────────────────────
  {
    id: 'dataset_catalog',
    selectors: [],
    evidenceDomains: ['public_data'],
    to: '/admin/public-data/datasets',
    label: `${VIEW_LABEL}.dataset_catalog`,
    menu: sectorMenu('public_administration'),
    sectorGroup: 'public_administration',
  },
  {
    id: 'sharing_register',
    selectors: [],
    evidenceDomains: ['public_data'],
    to: '/admin/public-data/sharing',
    label: `${VIEW_LABEL}.sharing_register`,
    menu: sectorMenu('public_administration'),
    sectorGroup: 'public_administration',
  },
  {
    id: 'open_data_releases',
    selectors: [],
    evidenceDomains: ['public_data'],
    to: '/admin/public-data/releases',
    label: `${VIEW_LABEL}.open_data_releases`,
    menu: sectorMenu('public_administration'),
    sectorGroup: 'public_administration',
  },
  // ── Program Governance: manufacturing ────────────────────────────────
  {
    id: 'product_register',
    selectors: [],
    evidenceDomains: ['product_conformity', 'product_traceability'],
    to: '/admin/manufacturing/products',
    label: `${VIEW_LABEL}.product_register`,
    menu: PRODUCT_COMPLIANCE_MENU,
    entryPointGroups: ['manufacturing'],
  },
  {
    id: 'batch_register',
    selectors: [],
    evidenceDomains: ['product_traceability'],
    to: '/admin/manufacturing/batches',
    label: `${VIEW_LABEL}.batch_register`,
    menu: sectorMenu('manufacturing'),
    sectorGroup: 'manufacturing',
  },
  {
    id: 'trace_timeline',
    selectors: [],
    evidenceDomains: ['product_traceability'],
    to: '/admin/manufacturing/trace',
    label: `${VIEW_LABEL}.trace_timeline`,
    menu: sectorMenu('manufacturing'),
    sectorGroup: 'manufacturing',
  },
  {
    id: 'conformity_evidence',
    selectors: [],
    evidenceDomains: ['product_conformity'],
    to: '/admin/manufacturing/conformity',
    label: `${VIEW_LABEL}.conformity_evidence`,
    menu: PRODUCT_COMPLIANCE_MENU,
    entryPointGroups: ['manufacturing'],
  },
  {
    id: 'epr_reconciliation',
    selectors: [],
    evidenceDomains: ['producer_responsibility'],
    to: '/admin/manufacturing/epr',
    label: `${VIEW_LABEL}.epr_reconciliation`,
    menu: sectorMenu('manufacturing'),
    sectorGroup: 'manufacturing',
  },
  {
    id: 'chemical_register',
    selectors: [],
    evidenceDomains: ['substance_control'],
    to: '/admin/manufacturing/chemicals',
    label: `${VIEW_LABEL}.chemical_register`,
    menu: sectorMenu('manufacturing'),
    sectorGroup: 'manufacturing',
  },
  {
    // Sprint 63.31.0 (#444, #445) — the declarations and code-termination
    // records, read from the form instances the rows of these two event types
    // link (`manufacturing/chemical-traceability-service.ts`).
    id: 'chemical_traceability_declarations',
    selectors: [
      CHEMICAL_TRACEABILITY_DECLARATION_EVENT_TYPE,
      CHEMICAL_TRACEABILITY_TERMINATION_EVENT_TYPE,
    ],
    to: '/admin/manufacturing/chemical-traceability',
    label: `${VIEW_LABEL}.chemical_traceability_declarations`,
    menu: sectorMenu('manufacturing'),
    sectorGroup: 'manufacturing',
  },
  // ── Program Governance: logistics ────────────────────────────────────
  {
    id: 'customs_evidence',
    selectors: [],
    evidenceDomains: ['customs_trade'],
    to: '/admin/logistics/customs',
    label: `${VIEW_LABEL}.customs_evidence`,
    menu: sectorMenu('transport_logistics'),
    sectorGroup: 'transport_logistics',
  },
  // Sprint 62.55.0 — the customs tax-administration registers. Each page reads
  // the installed templates that declare its surfaces
  // (`GET /customs-tax-administration/context/:surfaceKey`), one per tab.
  {
    id: 'customs_tax_dossiers',
    selectors: [
      CUSTOMS_TAX_REGISTER_SURFACES.dossier,
      CUSTOMS_TAX_REGISTER_SURFACES.receivedDocument,
      CUSTOMS_TAX_REGISTER_SURFACES.refundRecord,
      CUSTOMS_TAX_REGISTER_SURFACES.debtInstrument,
    ],
    to: '/admin/logistics/tax-dossiers',
    label: `${VIEW_LABEL}.customs_tax_dossiers`,
    menu: TRADE_CUSTOMS_MENU,
    entryPointGroups: ['transport_logistics'],
  },
  {
    id: 'customs_tax_payments',
    selectors: [CUSTOMS_TAX_REGISTER_SURFACES.payment, CUSTOMS_TAX_REGISTER_SURFACES.anchorEvent],
    to: '/admin/logistics/tax-payments',
    label: `${VIEW_LABEL}.customs_tax_payments`,
    menu: TRADE_CUSTOMS_MENU,
    entryPointGroups: ['transport_logistics'],
  },
  {
    id: 'customs_tax_duty_free_lists',
    selectors: [
      CUSTOMS_TAX_REGISTER_SURFACES.dutyFreeList,
      CUSTOMS_TAX_REGISTER_SURFACES.usageNotification,
    ],
    to: '/admin/logistics/duty-free-lists',
    label: `${VIEW_LABEL}.customs_tax_duty_free_lists`,
    menu: sectorMenu('transport_logistics'),
    sectorGroup: 'transport_logistics',
  },
  {
    id: 'customs_tax_examinations',
    selectors: [CUSTOMS_TAX_REGISTER_SURFACES.examination],
    to: '/admin/logistics/tax-examinations',
    label: `${VIEW_LABEL}.customs_tax_examinations`,
    menu: sectorMenu('transport_logistics'),
    sectorGroup: 'transport_logistics',
  },
  {
    id: 'customs_tax_transition',
    selectors: [CUSTOMS_TAX_REGISTER_SURFACES.projectTransfer],
    to: '/admin/logistics/tax-transition',
    label: `${VIEW_LABEL}.customs_tax_transition`,
    menu: sectorMenu('transport_logistics'),
    sectorGroup: 'transport_logistics',
  },
  {
    id: 'parcel_claims',
    selectors: [],
    evidenceDomains: ['postal_operations'],
    to: '/admin/logistics/claims',
    label: `${VIEW_LABEL}.parcel_claims`,
    menu: sectorMenu('transport_logistics'),
    sectorGroup: 'transport_logistics',
  },
  {
    id: 'postal_authorisations',
    selectors: [],
    evidenceDomains: ['postal_operations'],
    to: '/admin/logistics/authorisations',
    label: `${VIEW_LABEL}.postal_authorisations`,
    menu: sectorMenu('transport_logistics'),
    sectorGroup: 'transport_logistics',
  },
];

/**
 * A sidebar leaf in the places this registry covers that NO installed overlay
 * turns on, with the finding and where it is made. Not attached to an overlay
 * because the modal would then claim a dependency the screen does not have.
 * `__tests__/overlay-register-surfaces.test.ts` requires every such leaf to be
 * an entry above or an entry here, so a new leaf has to be decided.
 */
/**
 * Sprint 62.51.0 (CL-3) — the register view that holds records of a declared
 * surface, by the surface id a server names (a controlled-testing case's
 * `outcome_recorded_on`). Read from this registry, so the route a surface opens
 * is written once; `undefined` for a surface no view claims.
 */
export function registerViewForSurface(surface: string): OverlayRegisterEntry | undefined {
  return OVERLAY_REGISTER_VIEWS.find((view) => view.selectors.includes(surface));
}

export interface OverlayIndependentEntry {
  to: string;
  /** What the screen reads instead, and the file that shows it. */
  reason: string;
}

export const OVERLAY_INDEPENDENT_VIEWS: readonly OverlayIndependentEntry[] = [
  {
    // Sprint 63.43.0 — the Overview page, first leaf of Sector Overlays ›
    // Overlay Management. Module `sector_overlays` (`ROUTE_MODULE_MAP`).
    to: SECTOR_OVERLAYS_OVERVIEW_ROUTE,
    reason:
      'The Sector Overlays landing page: it lists the installed overlays by sector group from the navigation projection and this registry; no declaration fills it and it holds no records of its own.',
  },
  {
    // Sprint 63.43.0 — relocated to Sector Overlays › Overlay Management, URL
    // unchanged. Module `sector_overlays` (`ROUTE_MODULE_MAP`).
    to: SECTOR_OVERLAYS_INSTALLER_ROUTE,
    reason: 'It is the overlay catalog itself.',
  },
  {
    // Sprint 63.43.0 — Localization › Regulatory Registers (plan §2.2).
    to: '/admin/legal-holds',
    reason:
      'A cross-entity register an organisation places by hand; the destruction gates consult it for any record. `legal-hold-service.ts` reads no framework, deadline or guidance record, and the leaf is gated by the `legal_hold:read` grant and the `localization` module alone (`route-permissions.ts`).',
  },
  {
    to: '/admin/manufacturing/capa-recall',
    reason:
      'Applies no regime vocabulary: `capa-recall-service.ts` takes no regime and `CapaRecord` carries no `regime_source`.',
  },
  {
    to: '/admin/manufacturing/ot-assets',
    reason:
      'Industrial OT has no evidence domain (`ot-service.ts`, "This domain has no evidence regime"); it extends the canonical system store.',
  },
  {
    to: '/admin/manufacturing/remote-access',
    reason:
      'Industrial OT has no evidence domain (`ot-service.ts`); a grant names a vendor, not a regime.',
  },
  {
    to: '/admin/logistics/shipments',
    reason: 'Records parties and cargo; no service of the shipment register reads a regime.',
  },
  {
    to: '/admin/logistics/inspections',
    reason:
      'Authority inspections and holds are recorded in `clearance-service.ts`, which reads no regime; only customs evidence records are made under a customs regime.',
  },
  {
    to: '/admin/logistics/parcels',
    reason: 'The parcel register reads no regime (`parcel-service.ts`).',
  },
  {
    to: '/admin/logistics/delivery',
    reason:
      'The delivery timeline and proof read no regime (`parcel-event-service.ts`, `delivery-proof-service.ts`).',
  },
];

/**
 * Selectors a shipped optional pack declares that no screen reads (fix-sprint-
 * 20261016-2013 Finding 10). Each is listed with the evidence for that finding,
 * and is shown in the Feature views modal by its localized name
 * (`localization.sector_overlay.declared_kind_labels.<selector>`), never by its
 * machine code. `__tests__/overlay-register-surfaces.test.ts` fails when a shipped
 * optional pack declares a selector that is neither claimed by a view nor listed
 * here, when an entry here is claimed by a view or declared by no pack, and when
 * an entry has no name in English and Vietnamese.
 */
export const OVERLAY_DECLARED_WITHOUT_SCREEN: Readonly<Record<string, string>> = {
  biometric_technical_regulation_commencement:
    'The commencement clock of the identity-biometrics overlay (voice and iris technical regulations). Since Sprint 62.56.0 the three biometric capture registers exist, but they discover their frameworks by the form `register_surface` of their templates, not by this event type, and they raise no deadline of their own: the effective date they show is read off the framework record. No screen reads this event type (no reader of it in `src/`); the regulatory clock tracks the date.',
  chemical_traceability_commencement:
    'A dated clock of the chemicals-traceability overlay; no register reads it (no reader of the event type in `src/`).',
  consumer_complaint_receipt_acknowledgement:
    'Sprint 62.49.1 (DB-10; owner answer Q-C, 2026-10-30) — the acknowledgement-of-receipt period the consumer-rights overlay declares for a consumer complaint. Declared as a deadline row only: by the owner answer the food complaint register raises no clock from it, and no other register reads the event type (no reader of it in `src/`).',
  cybersecurity_fee_schedule_commencement:
    'The commencement clock of the cybersecurity fees overlay; no register reads it (no reader of the event type in `src/`).',
  food_database_traceability_commencement:
    'A dated clock of a food-traceability overlay; no register reads it (no reader of the event type in `src/`).',
  food_traceability_connection_start:
    'A dated clock of a food-traceability overlay; no register reads it (no reader of the event type in `src/`).',
  food_traceability_system_update:
    'A dated clock of a food-traceability overlay; no register reads it (no reader of the event type in `src/`).',
};

/** Resolve a dotted i18n path against the translation object; `''` when absent. */
export function resolveI18nPath(t: unknown, path: string): string {
  let cursor: unknown = t;
  for (const segment of path.split('.')) {
    if (cursor === null || typeof cursor !== 'object') return '';
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return typeof cursor === 'string' ? cursor : '';
}

/**
 * The ways one pack's declarations join one view, in a stable order. Empty when
 * the pack declares nothing the view reads.
 */
function basisOf(view: OverlayRegisterEntry, facts: OverlayPackFacts): OverlayViewBasis[] {
  const selectors = new Set(facts.register_surfaces ?? []);
  const declarations = facts.pack_declarations;
  const domains = new Set(declarations?.evidence_domains ?? []);
  const modules = new Set(declarations?.fine_area_modules ?? []);
  const basis: OverlayViewBasis[] = [];
  if (view.selectors.some((s) => selectors.has(s))) basis.push('records');
  if (
    (view.evidenceDomains ?? []).some((d) => domains.has(d)) ||
    (view.trustRegimes === true && declarations?.trust_regimes === true)
  ) {
    basis.push('regime');
  }
  if (
    (view.fineAreas === true && (declarations?.fine_area_count ?? 0) > 0) ||
    (view.fineAreaModules ?? []).some((m) => modules.has(m))
  ) {
    basis.push('fine_areas');
  }
  return basis;
}

/**
 * The views a pack's declarations bring into use, in registry order, each with
 * the kinds of declaration that joined it, and the declared selectors no view
 * claims (a clock nothing reads for this overlay, a record kind with no register
 * yet). The unclaimed ones are returned, not dropped, so the modal can say the
 * pack declares something with no dedicated screen rather than implying the
 * list is complete.
 */
export function viewsForDeclaredSurfaces(
  declared: readonly string[],
  facts: Omit<OverlayPackFacts, 'register_surfaces'> = {},
): {
  views: OverlayRegisterEntry[];
  unclaimed: string[];
  basis: Record<string, OverlayViewBasis[]>;
} {
  const declaredSet = new Set(declared);
  const all: OverlayPackFacts = { register_surfaces: declared, ...facts };
  const basis: Record<string, OverlayViewBasis[]> = {};
  const views = OVERLAY_REGISTER_VIEWS.filter((v) => {
    const kinds = basisOf(v, all);
    if (kinds.length > 0) basis[v.id] = kinds;
    return kinds.length > 0;
  });
  const unclaimed = [...declaredSet].filter(
    (s) => !OVERLAY_REGISTER_VIEWS.some((v) => v.selectors.includes(s)),
  );
  return { views, unclaimed: unclaimed.sort(), basis };
}

/**
 * Why a view is, or is not, in use for this organisation. Checked in this
 * order, and the first that applies wins:
 *
 * - `not_installed` — the overlay is not installed, so no register discovers
 *   its declarations;
 * - `reference` — installed for reference only; a register turns a reference
 *   install's declarations away (OD8 (a)), so it opens and reads quiet;
 * - `module_off` — installed Direct, but the module that owns the screen is
 *   switched off for the organisation, so the router refuses it;
 * - `enabled` — installed Direct and its module is on.
 */
export type OverlayViewState = 'enabled' | 'reference' | 'not_installed' | 'module_off';

export interface OverlayViewRow {
  view: OverlayRegisterEntry;
  /** The kinds of pack declaration that joined the view to this overlay. */
  basis: OverlayViewBasis[];
  state: OverlayViewState;
  /** The viewer's role opens the route (the same question the sidebar and `PermissionGate` ask). */
  canOpen: boolean;
}

const adminRelative = (to: string): string => to.replace(/^\/admin\/?/, '');

/**
 * Each view a pack's declarations bring into use, with its state for this
 * organisation and viewer. Pure: the caller passes the install facts and the
 * two live predicates (`isModuleEnabled` from the module config, `hasPermission`
 * from the session).
 */
export function deriveOverlayViewRows(
  entry: {
    installed: boolean;
    installed_as: string | null;
  } & OverlayPackFacts,
  isModuleEnabled: (moduleId: string) => boolean,
  hasPermission: (permission: string) => boolean,
): { rows: OverlayViewRow[]; unclaimed: string[] } {
  const { views, unclaimed, basis } = viewsForDeclaredSurfaces(entry.register_surfaces ?? [], {
    pack_declarations: entry.pack_declarations,
  });
  const rows = views.map((view): OverlayViewRow => {
    const relative = adminRelative(view.to);
    const moduleId = getRouteModule(relative);
    const moduleOn = !moduleId || isModuleEnabled(moduleId);
    const state: OverlayViewState = !entry.installed
      ? 'not_installed'
      : entry.installed_as !== 'direct'
        ? 'reference'
        : !moduleOn
          ? 'module_off'
          : 'enabled';
    return {
      view,
      basis: basis[view.id] ?? [],
      state,
      canOpen: routeOpensFor(relative, hasPermission),
    };
  });
  return { rows, unclaimed };
}
